import {
  setPaymentSuccess,
  setPaymentError,
  setPaymentIsProcessing,
  setApplyingPromoCode,
  setApplyingGiftCard,
  setGiftCardSuccess,
  setPromoCodeSuccess,
  setGiftCardError,
  setPromoCodeError,
  setSurchargeData,
} from "../../../store/Store";
import { fetchTicketData } from "../../general/AppHelpers";
import i18n from "i18next";
import { paymentFailedStatuses } from "../../general/constants";
import ApiWrapper from "../../general/ApiWrapper";
import { isCloud9SurchargePassthroughEnabled } from "../../surcharge/api";

const { t } = i18n;

export const processCloud9Payment = async ({
  accessToken,
  amount,
  selectedPaymentMethod,
  paymentToken,
  card_type,
  ticketData,
}) => {
  setPaymentIsProcessing(true);

  // Call the transactions API with the payment token or saved payment method
  const { deposit } = ticketData?.access_token?.data?.payload;
  const surchargeAmount = ticketData?.resource?.surcharge_amount || 0;
  const sendSurchargeSeparately =
    surchargeAmount > 0 && isCloud9SurchargePassthroughEnabled(ticketData);
  const paymentData = {
    payment_method_type: "card",
    amount,
    ...(sendSurchargeSeparately && { surcharge_amount: surchargeAmount }),
    ...(selectedPaymentMethod?.id
      ? { payment_method_id: selectedPaymentMethod.id }
      : { payment_token: paymentToken }),
    deposit,
  };

  const { data, error } = await ApiWrapper.post(
    "/api/mkp/v1/intake/transactions",
    paymentData,
    {
      accessToken,
      params: {
        card_type: card_type,
      },
    }
  );

  setPaymentIsProcessing(false);

  if (error) {
    setPaymentError({
      type: error?.type,
      message: error.message || t("payment.processing_error"),
    });
  } else if (paymentFailedStatuses.includes(data?.transaction?.status)) {
    setPaymentError({
      message:
        data?.transaction?.status === paymentFailedStatuses[0]
          ? t("payment.declined")
          : t("payment.failed"),
    });
  } else {
    setPaymentSuccess(t("payment.success_confirmation"));
    fetchTicketData(accessToken);
    setSurchargeData(null);
  }
};

export const createStripePaymentSession = async ({
  company,
  location,
  accessToken,
  amount,
  paymentAccount,
  callbackUrl,
}) => {
  return ApiWrapper.get("/api/mkp/v1/user/payment_methods/session", {
    accessToken,
    params: {
      company_id: company?.id,
      location_id: location?.id,
      callback_url: callbackUrl,
      payment_type: "card",
      add_to_client: false,
      guest: false,
      amount, // Already in dollars
      stripe_account_id: paymentAccount?.stripe_account_id,
    },
  });
};

export const processStripeExistingCard = async ({
  accessToken,
  amount,
  selectedPaymentMethod,
  ticketData,
}) => {
  setPaymentIsProcessing(true);

  const { deposit } = ticketData?.access_token?.data?.payload;

  const { data, error } = await ApiWrapper.post(
    "/api/mkp/v1/intake/transactions",
    {
      card_id: selectedPaymentMethod.id,
      payment_method_type: "card",
      amount,
      deposit,
    },
    { accessToken }
  );

  const status = data?.transaction?.status;
  setPaymentIsProcessing(false);

  if (error) {
    setPaymentError({
      type: error?.type,
      message: error.message || t("payment.saved_cc_payment_fail"),
    });
  } else if (paymentFailedStatuses.includes(status)) {
    setPaymentError({
      message:
        data?.transaction?.status === paymentFailedStatuses[0]
          ? t("payment.declined")
          : t("payment.failed"),
    });
  } else {
    setPaymentSuccess(t("payment.success_confirmation"));
    fetchTicketData(accessToken);
  }
};

export const applyGiftCard = async (code, companyId, accessToken) => {
  setApplyingGiftCard(true);

  const { error } = await ApiWrapper.post(
    "/api/mkp/v1/intake/coupons",
    {
      coupon_code: code,
      company_id: companyId,
    },
    { accessToken }
  );
  setApplyingGiftCard(false);

  if (error) {
    setGiftCardError(t("discounts.apply_gift_card_error"));
    return;
  }

  setGiftCardSuccess(t("discounts.apply_gift_card_success", { code }));
  fetchTicketData(accessToken);
};

// The BE sends a machine-readable reason code (e.g. "not_a_new_client") under
// errors.general[0].type, which ApiWrapper surfaces as error.type. Map it to a
// localized message; fall back to the generic message when there is no code
// (kill switch engaged) or the reason isn't translated in the active locale.
const promoCodeErrorMessage = (error) => {
  const { code, name, child_name: childName } = error?.type || {};
  if (!code) return t("discounts.apply_promo_code_error");

  return t(`discounts.promo_errors.${code}`, {
    name,
    child_name: childName?.toLowerCase(),
    defaultValue: t("discounts.apply_promo_code_error"),
  });
};

export const applyPromoCode = async (code, companyId, accessToken) => {
  setApplyingPromoCode(true);

  const { error } = await ApiWrapper.post(
    "/api/mkp/v1/intake/promotions",
    {
      promotion_code: code,
      company_id: companyId,
    },
    { accessToken }
  );
  setApplyingPromoCode(false);

  if (error) {
    setPromoCodeError(promoCodeErrorMessage(error));
    return;
  }

  setPromoCodeSuccess(t("discounts.apply_promo_code_success", { code }));
  fetchTicketData(accessToken);
};
